import React, { useMemo, useState } from "react";
import { useShiftLocations, processLocationData, calculateMapBounds } from "../../hooks/useShiftLocations";
import { useActorLocations, processActorLocationData, calculateActorMapBounds } from "../../hooks/useActorLocations";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import "react-datepicker/dist/react-datepicker.css";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useShiftEngagements, calculateEngagementStats } from "../../hooks/useShiftEngagements";
import { useShift } from "../../hooks/useShift";

// Custom styles for Leaflet markers
const leafletStyles = `
  .custom-marker {
    background: transparent !important;
    border: none !important;
  }
  .leaflet-popup-content-wrapper {
    border-radius: 8px;
  }
  .leaflet-popup-content {
    margin: 8px 12px;
  }
`;

// Inject styles into the document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = leafletStyles;
  document.head.appendChild(styleSheet);
}

interface LocationInfo {
  lat: number | null;
  lng: number | null;
  placeName: string;
}

type MappedLocation = {
  id: string | number;
  latitude: number | null;
  longitude: number | null;
  placeName?: string;
  label?: string;
  timestamp: string;
  activity: string;
  registrations: number;
  actorName?: string;
  shiftId?: string;
  shiftStatus?: string;
};

type ValidMappedLocation = Omit<MappedLocation, 'latitude' | 'longitude'> & {
  latitude: number;
  longitude: number;
};

function hasValidCoordinates(location: MappedLocation): location is ValidMappedLocation {
  return (
    typeof location.latitude === 'number' &&
    Number.isFinite(location.latitude) &&
    typeof location.longitude === 'number' &&
    Number.isFinite(location.longitude)
  );
}

const SAMPLE_SHIFT_HISTORY: Record<string, number> = {
  // ISO date: hours worked
  "2025-01-02": 2.5,
  "2025-01-05": 6,
  "2025-01-07": 3,
  "2025-01-12": 7.5,
  "2025-01-16": 5,
  "2025-01-19": 1.5,
};

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const SAMPLE_TRACK = [
  { lat: 6.5244, lng: 3.3792, label: "Start - Lagos" },
  { lat: 7.3775, lng: 3.9470, label: "En route - Abeokuta" },
  { lat: 9.0765, lng: 7.3986, label: "End - Abuja" },
];

function MapTracking({ shiftId }: { shiftId?: string }) {
  const [animateTrack, setAnimateTrack] = useState(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);

  // Get current shift status
  const { currentShift } = useShift();

  // Fetch real shift locations and engagements
  const { data: locationsData, isLoading: locationsLoading } = useShiftLocations(shiftId);
  const { data: engagementsData, isLoading: engagementsLoading } = useShiftEngagements(shiftId);

  const { data: actorLocationsData, isLoading: actorLocationsLoading } = useActorLocations();

  const isActive = currentShift?.status === 'ACTIVE' || currentShift?.status === 'PAUSED';

  const processedLocations = useMemo(() => {
    if (isActive && locationsData?.data) {
      return processLocationData(locationsData.data);
    } else if (!isActive && actorLocationsData?.data?.content) {
      return processActorLocationData(actorLocationsData.data.content);
    }
    return [];
  }, [isActive, locationsData, actorLocationsData]);

  // Calculate engagement statistics (only for active shifts)
  const engagementStats = useMemo(() => {
    if (!isActive || !engagementsData?.data) return { 
      totalRegistrations: 0, 
      todayRegistrations: 0,
      registeredCount: 0,
      unregisteredCount: 0,
      reminderCount: 0,
      registrationRate: 0,
    };
    return calculateEngagementStats(engagementsData.data);
  }, [isActive, engagementsData]);

  // Use sample data if no real data available
  const displayLocations = processedLocations.length > 0 ? processedLocations : SAMPLE_TRACK.map((loc, index) => ({
    ...loc,
    id: `sample-${index}`,
    timestamp: new Date().toISOString(),
    activity: index === 0 ? 'Shift Start' : index === SAMPLE_TRACK.length - 1 ? 'Shift End' : 'Canvassing',
    registrations: 0,
    latitude: loc.lat,
    longitude: loc.lng,
  }));

  const validLocations = useMemo(
    () =>
      (displayLocations as MappedLocation[]).filter(hasValidCoordinates),
    [displayLocations]
  );

  // Dynamic title and description based on shift status
  const mapTitle = isActive ? 'Current Shift Tracking' : "Today's Canvassing tracks";
  const mapDescription = isActive 
    ? 'Tracking your current shift location in real-time'
    : 'Showing all locations from your completed shifts for today';

  const selectedCanvasser = {
    id: 1,
    canvasserName: isActive ? 'Current Shift' : 'All Historical Shifts',
    date: new Date().toLocaleDateString(),
    locations: validLocations,
  };

  // Custom icons for different activities
  const createCustomIcon = (activity: string, registrations: number) => {
    let iconColor = '#3B82F6'; // Default blue
    
    if (activity === 'Shift Start') iconColor = '#10B981'; // Green
    else if (activity === 'Shift End') iconColor = '#EF4444'; // Red
    else if (activity === 'Lunch Break') iconColor = '#F59E0B'; // Orange
    else if (registrations > 15) iconColor = '#8B5CF6'; // Purple for high performance
    
    return L.divIcon({
      html: `<div style="
        background-color: ${iconColor};
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      "></div>`,
      className: 'custom-marker',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });
  };

  // Get track coordinates for polyline
  const trackCoordinates = selectedCanvasser.locations.map(loc => [loc.latitude, loc.longitude] as [number, number]);

  // Calculate map bounds to fit all points
  const bounds = trackCoordinates.length > 0
    ? L.latLngBounds(trackCoordinates)
    : L.latLngBounds([[SAMPLE_TRACK[0].lat, SAMPLE_TRACK[0].lng]]);

  return (
    <div className="space-y-4">
      {/* Map header with dynamic title */}
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900">{mapTitle}</h3>
        <p className="text-sm text-gray-600 mt-1">{mapDescription}</p>
      </div>

      {/* Map container */}
      <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-300">
        <MapContainer
          bounds={bounds}
          boundsOptions={{ padding: [20, 20] }}
          center={[SAMPLE_TRACK[0].lat, SAMPLE_TRACK[0].lng]}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Draw the track polyline */}
          {trackCoordinates.length > 1 && (
            <Polyline
              positions={trackCoordinates}
              color="#3B82F6"
              weight={3}
              opacity={0.7}
              dashArray={animateTrack ? "10, 10" : undefined}
            />
          )}
          
          {/* Markers for each location */}
          {selectedCanvasser.locations.map((location, index) => (
            <Marker
              key={index}
              position={[location?.latitude, location?.longitude]}
              icon={createCustomIcon(location.activity, location.registrations)}
            >
              <Popup>
                <div className="text-sm">
                  <div className="font-semibold text-gray-900">{'placeName' in location ? location.placeName : location.label}</div>
                  <div className="text-gray-600 mt-1">
                    <div className="flex items-center gap-1">
                      <AccessTimeIcon style={{ fontSize: 14 }} />
                      {new Date(location.timestamp).toLocaleString()}
                    </div>
                    <div className="mt-1">
                      <span className="font-medium">Activity:</span> {location.activity}
                    </div>
                    {isActive && (
                      <div>
                        <span className="font-medium">Registrations:</span>{' '}
                        <span className={location.registrations > 0 ? 'text-green-600 font-semibold' : 'text-gray-500'}>
                          {location.registrations}
                        </span>
                      </div>
                    )}
                    {!isActive && (location as any).actorName && (
                      <div>
                        <span className="font-medium">Canvasser:</span> {(location as any).actorName}
                      </div>
                    )}
                    {!isActive && (location as any).shiftStatus && (
                      <div>
                        <span className="font-medium">Shift Status:</span>{' '}
                        <span className={`font-semibold ${
                          (location as any).shiftStatus === 'ACTIVE' ? 'text-green-600' : 
                          (location as any).shiftStatus === 'PAUSED' ? 'text-amber-600' : 
                          'text-gray-600'
                        }`}>
                          {(location as any).shiftStatus}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Track summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        {/* Collapsible header */}
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
        >
          <h4 className="font-semibold text-gray-900">
            {isActive ? 'Current Shift Summary' : 'Historical Summary'}
          </h4>
          <div className="flex items-center text-gray-500 hover:text-gray-700 transition-colors">
            {isSummaryExpanded ? (
              <ExpandLessIcon style={{ fontSize: 20 }} />
            ) : (
              <ExpandMoreIcon style={{ fontSize: 20 }} />
            )}
          </div>
        </div>
        
        {/* Collapsible content */}
        {isSummaryExpanded && (
          <div className="mt-4 space-y-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <div>
                <span className="text-gray-600">Total Locations:</span>
                <span className="font-semibold ml-2">
                  {(locationsLoading || actorLocationsLoading) ? '...' : selectedCanvasser.locations.length}
                </span>
              </div>
              {isActive && (
                <div>
                  <span className="text-gray-600">Total Registrations:</span>
                  <span className="font-semibold ml-2 text-green-600">
                    {engagementsLoading ? '...' : engagementStats.totalRegistrations}
                  </span>
                </div>
              )}
              <div>
                <span className="text-gray-600">Start Time:</span>
                <span className="font-semibold ml-2">
                  {selectedCanvasser.locations.length > 0 
                    ? new Date(selectedCanvasser.locations[0].timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : '--:--'
                  }
                </span>
              </div>
              <div>
                <span className="text-gray-600">End Time:</span>
                <span className="font-semibold ml-2">
                  {isActive 
                    ? '--:--'
                    : selectedCanvasser.locations.length > 0 
                      ? new Date(selectedCanvasser.locations[selectedCanvasser.locations.length - 1].timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : '--:--'
                  }
                </span>
              </div>
            </div>
            
            {/* Additional stats - only show for active shifts */}
            {isActive && (
              <div className="pt-4 border-t border-gray-200 flex flex-wrap gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Today's Registrations:</span>
                  <span className="font-semibold ml-2 text-blue-600">
                    {engagementsLoading ? '...' : engagementStats.todayRegistrations}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Registered Voters:</span>
                  <span className="font-semibold ml-2 text-green-600">
                    {engagementsLoading ? '...' : engagementStats.registeredCount}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Reminder Opt-ins:</span>
                  <span className="font-semibold ml-2 text-amber-600">
                    {engagementsLoading ? '...' : engagementStats.reminderCount}
                  </span>
                </div>
              </div>
            )}
            
            {/* Historical stats for non-active shifts */}
            {!isActive && actorLocationsData?.data && (
              <div className="pt-4 border-t border-gray-200 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total Records:</span>
                  <span className="font-semibold ml-2 text-blue-600">
                    {actorLocationsLoading ? '...' : actorLocationsData.data.totalElements}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Shifts Tracked:</span>
                  <span className="font-semibold ml-2 text-purple-600">
                    {actorLocationsLoading ? '...' : 
                      new Set(actorLocationsData.data.content.map(loc => loc.shift.id)).size
                    }
                  </span>
                </div>
                {/* <div>
                  <span className="text-gray-600">Date Range:</span>
                  <span className="font-semibold ml-2 text-gray-600">
                    {actorLocationsLoading ? '...' : 
                      actorLocationsData.data.content.length > 0 ? (
                        `${new Date(actorLocationsData.data.content[actorLocationsData.data.content.length - 1].recordedAt).toLocaleDateString()} - ${new Date(actorLocationsData.data.content[0].recordedAt).toLocaleDateString()}`
                      ) : 'No data'
                    }
                  </span>
                </div> */}
              </div>
            )}
            
            {/* Legend */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-xs text-gray-600 font-medium mb-2">Marker Colors:</div>
              <div className="flex flex-wrap gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span>Shift Start</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>Canvassing</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span>Lunch Break</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span>High Performance (15+)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span>Shift End</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MapTracking;
